import React, { useState, useEffect } from 'react';
import '@fontsource/inter';
import '@fontsource/poppins';
import '@fontsource/open-sans';

// Components
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Features', href: '#features' },
    { name: 'How It Works', href: '#how-it-works' },
    { name: 'Technology', href: '#technology' },
    { name: 'Why Choose Us', href: '#benefits' }
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-accent py-2 shadow-md' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <a href="/" className="text-background font-display text-2xl font-bold">Grenora</a>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="text-background hover:text-secondary transition-colors">
                {link.name}
              </a>
            ))}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-background focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          
          {/* CTA Button */}
          <a 
            href="/signup"
            className="hidden md:inline-block btn btn-secondary text-sm"
          >
            Get Started
          </a>
        </div>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 mt-2 bg-primary/90 rounded-lg">
            <div className="flex flex-col space-y-4 px-4">
              {navLinks.map((link) => (
                <a 
                  key={link.name} 
                  href={link.href} 
                  className="text-background hover:text-secondary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a 
                href="/signup"
                className="btn btn-secondary text-sm inline-block"
              >
                Get Started
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Hero = ({ headline, subheadline, primaryCta, secondaryCta }: {
  headline: string;
  subheadline: string;
  primaryCta: { text: string; href: string };
  secondaryCta: { text: string; onClick: () => void };
}) => (
  <section className="min-h-screen flex items-center relative overflow-hidden">
    {/* Background Video */}
    <div className="absolute inset-0 z-0">
      <video 
        autoPlay 
        muted 
        loop 
        className="w-full h-full object-cover brightness-50"
      >
        <source src="/videos/525250_Pov_Nature_1920x1080.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    
    {/* Gradient Overlay */}
    <div className="absolute inset-0 z-0 bg-gradient-to-b from-primary/30 to-primary/70"></div>
    
    <div className="container mx-auto px-4 z-10 relative">
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="font-display font-bold mb-6 text-background">
          {headline}
        </h1>
        <p className="text-xl md:text-2xl mb-8 text-background">
          {subheadline}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a 
            href={primaryCta.href} 
            className="btn btn-secondary text-lg inline-flex items-center justify-center"
          >
            {primaryCta.text}
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
          <button 
            onClick={secondaryCta.onClick} 
            className="btn bg-transparent border-2 border-background text-background hover:bg-background/10 text-lg inline-flex items-center justify-center"
          >
            {secondaryCta.text}
          </button>
        </div>
      </div>
    </div>
  </section>
);

const FeatureCard = ({ title, desc, icon }: {
  title: string;
  desc: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-background rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
    <div className="text-accent mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-2 text-primary">{title}</h3>
    <p className="text-text/80">{desc}</p>
  </div>
);

const StepCard = ({ number, title, description, image }: {
  number: number;
  title: string;
  description: string;
  image: string;
}) => (
  <div className="bg-background rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
    <div className="h-48 overflow-hidden">
      <img src={image} alt={title} className="w-full h-full object-cover object-center" />
    </div>
    <div className="p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-background flex items-center justify-center font-bold text-xl">
          {number}
        </div>
        <h3 className="text-xl font-bold text-primary pt-3">{title}</h3>
      </div>
      <p className="text-text/80">{description}</p>
    </div>
  </div>
);

const BenefitCard = ({ title, description, icon }: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-background rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
    <div className="text-accent2 mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-bold mb-2 text-primary">{title}</h3>
    <p className="text-text/80">{description}</p>
  </div>
);

const TechHighlight = ({ title, description, icon }: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) => (
  <div className="bg-primary/5 p-6 rounded-lg flex gap-4">
    <div className="text-accent flex-shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="text-xl font-bold mb-2 text-primary">{title}</h3>
      <p className="text-text/80">{description}</p>
    </div>
  </div>
);

const DemoForm = ({ onClose }: { onClose: () => void }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, you would send the form data to your backend here
    console.log('Form submitted:', formData);
    alert('Demo request submitted! We will contact you shortly.');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl p-6 md:p-8 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-text/60 hover:text-primary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <h2 className="text-2xl font-bold text-primary mb-6">Request a Demo</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-text mb-1">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-highlight rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text mb-1">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-highlight rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-text mb-1">Company *</label>
              <input
                type="text"
                id="company"
                name="company"
                required
                value={formData.company}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-highlight rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-text mb-1">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-highlight rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-text mb-1">Additional Information</label>
            <textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-highlight rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function App() {
  const [showDemoForm, setShowDemoForm] = useState(false);

  // Data
  const features = [
    { 
      title: "Smart Irrigation", 
      desc: "Adaptive watering schedules from soil to cloud.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ) 
    },
    { 
      title: "Precision Fertilization", 
      desc: "Drone-driven NDVI maps for exact nutrient placement.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ) 
    },
    { 
      title: "Crop Rotation Planning", 
      desc: "Multi-year strategies for soil health.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ) 
    },
    { 
      title: "Real-Time Monitoring", 
      desc: "Live dashboards of IoT sensors and weather data.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ) 
    },
  ];

  const steps = [
    {
      number: 1,
      title: "Connect Your Devices",
      description: "Seamless MQTT integration with any regionally available sensors.",
      image: "/images/connect your devices.jpg"
    },
    {
      number: 2,
      title: "Analyze & Optimize",
      description: "AI agents process data to generate irrigation and fertilization plans.",
      image: "/images/analyze&optimize.jpg"
    },
    {
      number: 3,
      title: "Act & Monitor",
      description: "Remote actuation and live dashboards enable hands-off, precision farm management.",
      image: "/images/act & monitor.jpeg"
    }
  ];

  const benefits = [
    {
      title: "Water Conservation",
      description: "Grenora reduces water usage by up to 27% per irrigation cycle, conserving resources and cutting operational costs.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Cost Reduction",
      description: "Precision fertilization recommendations lower fertilizer expenses by 25%, minimizing environmental impact and maximizing yield.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Yield Increase",
      description: "Smart crop rotation and monitoring can increase total harvest yields by up to 22% over traditional farming methods.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: "Environmental Impact",
      description: "Reduce chemical runoff by 35% with precise application timing and quantities determined by AI analysis.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const techHighlights = [
    {
      title: "IoT Telemetry",
      description: "Grenora's platform integrates real-time IoT telemetry (soil moisture, pH) via an MQTT ingestion pipeline.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      )
    },
    {
      title: "Explainable-AI",
      description: "Explainable-AI modules (SHAP, counterfactual analysis) quantify the impact of each agronomic intervention.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      title: "Digital-Twin Simulations",
      description: "Digital-twin simulations enable \"what-if\" scenario testing without risking actual crop performance.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      )
    }
  ];

  return (
    <div className="App hide-scrollbar">
      {/* Meta Tags */}
      <head>
        <title>Grenora - AI-Driven Farm Efficiency</title>
        <meta name="description" content="Empower your farm with AI-driven efficiency. Start your free trial today and slash water use by 20%." />
        <meta property="og:title" content="Grenora - AI-Driven Farm Efficiency" />
        <meta property="og:description" content="Empower your farm with AI-driven efficiency. Start your free trial today and slash water use by 20%." />
        <meta property="og:image" content="/og-image.jpg" />
        <meta property="og:url" content="https://grenora.com" />
        <meta name="twitter:card" content="summary_large_image" />
      </head>

      {/* Google Analytics */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-XXXXXXX');
          `,
        }}
      />

      {/* Demo Request Form */}
      {showDemoForm && <DemoForm onClose={() => setShowDemoForm(false)} />}

      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <Hero
        headline="Empower Your Farm with AI-Driven Efficiency"
        subheadline="Reduce Ressource and Increase Profit with Realtime Science Based Agro-Management"
        primaryCta={{ text: "How It Works", href: "#how-it-works" }}
        secondaryCta={{ text: "Request a Demo", onClick: () => setShowDemoForm(true) }}
      />

      {/* Features Section */}
      <section id="features" className="section bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-display font-bold text-primary mb-4">Innovative Farm Management Features</h2>
            <p className="text-lg text-text/80">Our cutting-edge technology transforms your agricultural operations with these powerful capabilities.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                title={feature.title}
                desc={feature.desc}
                icon={feature.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section bg-primary/5">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-display font-bold text-primary mb-4">How It Works</h2>
            <p className="text-lg text-text/80">Get started in minutes with our simple three-step process.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <StepCard 
                key={index}
                number={step.number}
                title={step.title}
                description={step.description}
                image={step.image}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Tech Highlights Section */}
      <section id="technology" className="section bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-display font-bold text-primary mb-4">Technology Highlights</h2>
            <p className="text-lg text-text/80">Our platform leverages cutting-edge technology to deliver results.</p>
          </div>
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {techHighlights.map((tech, index) => (
              <TechHighlight
                key={index}
                title={tech.title}
                description={tech.description}
                icon={tech.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section - Updated to "Why Choose Grenora" */}
      <section id="benefits" className="section bg-primary/5">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-display font-bold text-primary mb-4">Why Choose Grenora</h2>
            <p className="text-lg text-text/80">Our customers see measurable improvements in efficiency, yield, and environmental impact.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                title={benefit.title}
                description={benefit.description}
                icon={benefit.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary text-background">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display font-bold mb-6">Start Your Free Trial Today</h2>
            <p className="text-xl mb-8 text-background/80">
              Join thousands of farmers already using Grenora to optimize their operations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/signup" className="btn bg-secondary text-primary hover:bg-opacity-90">
                Start Your Free Trial
              </a>
              <button 
                onClick={() => setShowDemoForm(true)}
                className="btn bg-transparent border border-background text-background hover:bg-background/10"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-background py-12">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-bold text-xl mb-4">Grenora</h4>
              <p className="text-background/80">
                Empowering farms with AI-driven efficiency since 2024.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="/features" className="text-background/80 hover:text-background">Features</a></li>
                <li><a href="/pricing" className="text-background/80 hover:text-background">Pricing</a></li>
                <li><a href="/case-studies" className="text-background/80 hover:text-background">Case Studies</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="/about" className="text-background/80 hover:text-background">About Us</a></li>
                <li><a href="/careers" className="text-background/80 hover:text-background">Careers</a></li>
                <li><a href="/contact" className="text-background/80 hover:text-background">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-xl mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy" className="text-background/80 hover:text-background">Privacy Policy</a></li>
                <li><a href="/terms" className="text-background/80 hover:text-background">Terms of Service</a></li>
                <li><a href="/cookies" className="text-background/80 hover:text-background">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/20 mt-12 pt-8 text-center">
            <p className="text-background/60">
              &copy; {new Date().getFullYear()} Grenora, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
